import React, {useMemo, useState, useEffect} from "react";
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {ThemeProps, RTLProps} from '@/shared/prop-types/ReducerProps';
import {
    Modal,
    ModalBody,
} from "reactstrap";
import {Button, Form} from 'antd';
import CloseButton from "@/shared/components/CloseButton";
import {Space, Table} from 'antd';
import {Col, Row, Checkbox, Input} from 'antd';
import axios from "axios";
import SaveHierarchyModal from "./event/SaveHierarchyModal";
import SaveSelectedHierarchyModal from "./event/SaveSelectedHierarchyModal";

const AwardLevelsModal = ({isOpen, setOpen, toggle, theme, rtl, organization, data}) => {
    const [dataItems, setDataItems] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [awardLevelName, setAwardLevelName] = useState('');
    const [useAwardLevels, setUseAwardLevels] = useState(false);
    const [pendingValues, setPendingValues] = useState(null);
    const [pendingCheckbox, setPendingCheckbox] = useState(null);

    // Hierarchy modals state
    const [isOpenHierarchy, setIsOpenHierarchy] = useState(false);
    const [isOpenHierarchyList, setIsOpenHierarchyList] = useState(false);
    const [hierarchyListData, setHierarchyListData] = useState([]);
    const [selectedPrograms, setSelectedPrograms] = useState({});
    const [saveMessage, setSaveMessage] = useState(null);
    const [hierarchyMode, setHierarchyMode] = useState('award_level'); // 'award_level' | 'use_award_levels'

    useEffect(() => {
        if (isOpen) {
            fetchProgram();
        }
    }, [isOpen]);

    const fetchProgram = async () => {
        const response = await axios.get(
            `/organization/${data.organization_id}/program/${data.id}`
        );
        const program = response.data?.data ?? response.data;
        setUseAwardLevels(Boolean(Number(program.use_award_levels)));
    };

    const [awardLevel, setAwardLevel] = useState({
        id: 0,
        name: "",
        number_of_participants: 0,
        program_account_holder_id: null,
        program_id: 0,
        updated_at: "",
        created_at: "",
        v2id: null
    });
    const [action, setAction] = useState('index');

    const getDataItems = async () => {
        const response = await axios.get(
            `/organization/${data.organization_id}/program/${data.id}/program-award-levels`,
        );
        setDataItems(response.data)
    };

    const createItems = async (fValue) => {
        const response = await axios.post(
            `/organization/${data.organization_id}/program/${data.id}/create-award-level`, fValue);
        if (response.data.success){
            getDataItems();
            setAction('index');
            setAwardLevelName('');
            setSaveMessage({type: 'success', text: 'Award level saved successfully.'});
        }
    };

    const getParticipants = async (id) => {
        const response = await axios.post(
            `/organization/${data.organization_id}/program/${data.id}/award-level-participants`, {
                id: id
            });
        setParticipants(response.data.data);
        setAction('view')
    };

    useEffect(() => {
        getDataItems();
    }, []);

    const applyUseAwardLevels = async (newValue) => {
        await axios.put(
            `/organization/${data.organization_id}/program/${data.id}/use-award-levels`,
            { use_award_levels: newValue }
        );
        fetchProgram();
    };

    const onFinish = (values) => {
        setPendingValues(values);
        setHierarchyMode('award_level');
        setIsOpenHierarchy(true);
    };

    const onFinishFailed = (errorInfo: any) => {};

    // Save only to current program
    const onSubmitCurrentOnly = async () => {
        setIsOpenHierarchy(false);
        if (hierarchyMode === 'award_level' && pendingValues) {
            await createItems(pendingValues);
        } else if (hierarchyMode === 'use_award_levels' && pendingCheckbox !== null) {
            try {
                await applyUseAwardLevels(pendingCheckbox);
            } catch {
                setUseAwardLevels(!pendingCheckbox);
            }
            setPendingCheckbox(null);
        }
    };

    // Load hierarchy list and show selection modal
    const onSubmitHierarchy = async () => {
        setIsOpenHierarchy(false);

        const response = await axios.get(
            `/organization/${data.organization_id}/program/${data.id}/award-level-hierarchy-prepare`,
            { params: { name: hierarchyMode === 'award_level' ? pendingValues?.name : '' } }
        );

        const listData = response.data;
        const initial = {};
        listData.forEach(p => { initial[p.id] = true; });
        setSelectedPrograms(initial);
        setHierarchyListData(listData);
        setIsOpenHierarchyList(true);
    };

    // Save to selected programs in hierarchy
    const onSubmitHierarchyList = async () => {
        setIsOpenHierarchyList(false);

        const programIds = Object.keys(selectedPrograms)
            .filter(id => selectedPrograms[id])
            .map(Number);

        if (hierarchyMode === 'award_level' && pendingValues) {
            await createItems(pendingValues);
            const response = await axios.put(
                `/organization/${data.organization_id}/program/${data.id}/award-level-hierarchy`,
                { name: pendingValues.name, program_ids: programIds }
            );
            if (response.data.success) {
                setSaveMessage({type: 'success', text: `Award level saved to ${programIds.length} program(s).`});
            }
            setPendingValues(null);
        } else if (hierarchyMode === 'use_award_levels' && pendingCheckbox !== null) {
            try {
                await applyUseAwardLevels(pendingCheckbox);
                await axios.put(
                    `/organization/${data.organization_id}/program/${data.id}/use-award-levels-hierarchy`,
                    { use_award_levels: pendingCheckbox, program_ids: programIds }
                );
                setSaveMessage({type: 'success', text: `Use Award Levels updated for ${programIds.length} program(s).`});
            } catch {
                setUseAwardLevels(!pendingCheckbox);
            }
            setPendingCheckbox(null);
        }
    };

    const allSelected = hierarchyListData.length > 0 &&
        hierarchyListData.every(p => !!selectedPrograms[p.id]);

    const toggleSelectAll = (checked) => {
        const next = {};
        hierarchyListData.forEach(p => { next[p.id] = checked; });
        setSelectedPrograms(next);
    };

    const columnsHierarchyList = [
        {
            title: <Checkbox checked={allSelected} onChange={(e) => toggleSelectAll(e.target.checked)} />,
            dataIndex: 'id',
            key: 'checkbox',
            width: 40,
            render: (id) => (
                <Checkbox
                    checked={!!selectedPrograms[id]}
                    onChange={(e) => setSelectedPrograms(prev => ({ ...prev, [id]: e.target.checked }))}
                />
            ),
        },
        {
            title: 'Program',
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <span style={{ paddingLeft: (record.depth || 0) * 20 }}>
                    {record.depth > 0 ? <span style={{ color: '#999', marginRight: 4 }}>{'· '.repeat(record.depth)}</span> : null}{name}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'has_level',
            key: 'has_level',
            render: (val) => val ? 'Will be updated' : 'Will be added',
        },
    ];

    const viewAwardLevels = (record: any) => {
        setAwardLevelName('/'+record.name);
        getParticipants(record.id)
    };

    const editAwardLevels = (record: any) => {
        setAction('edit');
        setAwardLevel(record);
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <a>{text}</a>,
        },
        {
            title: 'Number of Participants',
            dataIndex: 'number_of_participants',
            key: 'number_of_participants',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <>
                    <Space size="middle" style={{color: '#70bbfd', cursor: "pointer"}} onClick={()=>editAwardLevels(record)}>Edit</Space> &ensp;
                    <Space size="middle" style={{color: '#70bbfd', cursor: "pointer"}} onClick={()=>viewAwardLevels(record)}>View</Space>
                </>
            ),
        },
    ];

    const columnsParticipants = [
        {
            title: 'Display Name',
            dataIndex: 'name',
            key: 'name',
            render: (_, record) => (
                <span size="middle">{record.first_name} {record.last_name}</span>
            ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Space size="middle" style={{color: '#70bbfd', cursor: "pointer"}} onClick={()=>window.open(`/program/${data.id}/user/view/${record.id}`, '_blank')} >View</Space>
            ),
        },
    ];

    return (
        <>
        <Modal
            className={`modal-program modal-lg ${theme.className} ${rtl.direction}-support`}
            isOpen={isOpen}
            toggle={toggle}
        >
            <CloseButton onClick={toggle}/>
            <ModalBody className="modal-lg">
                <Row>
                    <Col span={24}>
                        <h3>Award Levels</h3>
                        <h5 className="colorgrey">Programs / Program's Award Levels{awardLevelName}</h5>
                    </Col>
                </Row>
                {saveMessage && (
                    <Row style={{ marginTop: 10 }}>
                        <Col span={24}>
                            <div style={{ color: saveMessage.type === 'success' ? 'green' : 'red' }}>
                                {saveMessage.text}
                            </div>
                        </Col>
                    </Row>
                )}
                {action === 'index' && (
                    <>
                        <Row style={{ marginTop: 15 }}>
                            <Col md={6} lg={4} xl={4}>
                                <Checkbox
                                    checked={useAwardLevels}
                                    onChange={(e) => {
                                        const newValue = e.target.checked;
                                        setUseAwardLevels(newValue);
                                        setPendingCheckbox(newValue);
                                        setHierarchyMode('use_award_levels');
                                        setSaveMessage(null);
                                        setIsOpenHierarchy(true);
                                    }}
                                >
                                    Use Award Levels
                                </Checkbox>
                            </Col>
                        </Row>
                        {useAwardLevels && (
                        <>
                        <Row>
                            <Col span={20}></Col>
                            <Col span={4}>
                                <Button style={{marginLeft: 20, marginBottom: 5}} type="primary"
                                        onClick={() => { setAction('edit'); setSaveMessage(null); }}>New Award Level</Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <Table columns={columns} dataSource={dataItems}/>
                            </Col>
                        </Row></>
                        )}
                    </>
                )}
                {action === 'edit' && (
                    <Form
                        name="basic"
                        labelCol={{span: 8}}
                        wrapperCol={{span: 16}}
                        style={{maxWidth: 600, marginTop: 10}}
                        initialValues={{remember: true}}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        autoComplete="off"
                    >
                        <Row>
                            <Col span={17}>
                                <Form.Item
                                    name="program_id"
                                    style={{display: 'none'}}
                                    initialValue={data.id}
                                >
                                    <Input/>
                                </Form.Item>
                                <Form.Item
                                    name="id"
                                    style={{display: 'none'}}
                                    initialValue={awardLevel.id}
                                >
                                    <Input/>
                                </Form.Item>
                                <Form.Item
                                    label="Award Level Name"
                                    name="name"
                                    rules={[{required: true, message: 'Please input your "Award Level Name"'}]}
                                    initialValue={awardLevel.name}
                                >
                                    <Input/>
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item wrapperCol={{offset: 8, span: 16}}>
                                    <Button onClick={() => {
                                        setAction('index');
                                        setAwardLevelName('');
                                        setSaveMessage(null);
                                    }}>
                                        Close
                                    </Button>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item wrapperCol={{offset: 8, span: 16}}>
                                    <Button type="primary" htmlType="submit">
                                        Save
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                )}

                {action === 'view' && (
                    <>
                        <Row>
                            <Col span={21}>
                            </Col>
                            <Col span={3}>
                                <Form.Item wrapperCol={{offset: 8, span: 16}}>
                                    <Button onClick={() => {
                                        setAction('index');
                                        setAwardLevelName('');
                                    }}>
                                        Close
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row>
                            <Col span={24}>
                                <Table columns={columnsParticipants} dataSource={participants}/>
                            </Col>
                        </Row>
                    </>
                )}
            </ModalBody>
        </Modal>

        <SaveHierarchyModal
            isOpenHierarchy={isOpenHierarchy}
            toggleHierarchy={() => setIsOpenHierarchy(false)}
            onSubmit={onSubmitCurrentOnly}
            onSubmitHierarchy={onSubmitHierarchy}
            promptText="Would you like to save this award level across the entire program hierarchy?"
            onlyThisLabel="No, only save to this program"
            hierarchyLabel="Yes, save to Hierarchy"
        />

        <SaveSelectedHierarchyModal
            isOpenHierarchyList={isOpenHierarchyList}
            toggleHierarchyList={() => setIsOpenHierarchyList(false)}
            onSubmitHierarchyList={onSubmitHierarchyList}
            columnsHierarchyList={columnsHierarchyList}
            hierarchyListData={hierarchyListData}
            promptText="Please select the programs that you wish to add or update this award level"
        />
        </>
    );
};

AwardLevelsModal.propTypes = {
    theme: ThemeProps.isRequired,
    rtl: RTLProps.isRequired,
    organization: Object.isRequired,
    data: Object.isRequired
};

export default withRouter(connect((state) => ({
    theme: state.theme,
    rtl: state.rtl,
    organization: state.organization,
    data: state.program
}))(AwardLevelsModal));
